import React from 'react';
import { ChevronDown, type LucideIcon } from 'lucide-react';

interface HelpSectionProps {
  id: string;
  icon: LucideIcon;
  title: string;
  summary: string;
  bullets: string[];
  tips?: string[];
  tipsLabel: string;
  isExpanded: boolean;
  onToggle: () => void;
}

const HelpSection: React.FC<HelpSectionProps> = ({
  id,
  icon: Icon,
  title,
  summary,
  bullets,
  tips,
  tipsLabel,
  isExpanded,
  onToggle,
}) => {
  return (
    <div
      id={`help-section-${id}`}
      className="border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-900 overflow-hidden"
    >
      {/* Header (always visible) */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
      >
        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
          <Icon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-zinc-900 dark:text-white">
            {title}
          </h3>
          {!isExpanded && (
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-0.5 truncate">
              {summary}
            </p>
          )}
        </div>
        <ChevronDown
          className={`h-5 w-5 text-zinc-400 dark:text-zinc-500 flex-shrink-0 transition-transform duration-200 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {/* Expandable content */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-0">
          <p className="text-sm text-zinc-600 dark:text-zinc-300 mb-3">
            {summary}
          </p>

          {/* Bullet list */}
          <ul className="space-y-2">
            {bullets.map((bullet, i) => (
              <li key={i} className="flex gap-2.5 text-sm text-zinc-700 dark:text-zinc-300">
                <span className="flex-shrink-0 mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 dark:bg-blue-400" />
                <span>{bullet}</span>
              </li>
            ))}
          </ul>

          {/* Optional tips box */}
          {tips && tips.length > 0 && (
            <div className="mt-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wider mb-1.5">
                {tipsLabel}
              </p>
              <ul className="space-y-1.5">
                {tips.map((tip, i) => (
                  <li key={i} className="text-sm text-amber-800 dark:text-amber-300">
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HelpSection;
